const STORAGE_PREFIX = "hive:grid-session-state";

export const GRID_SESSION_KEYS = {
    opportunities: "opportunities",
    contacts: "contacts",
    contactOpportunities: "contact-opportunities",
    customers: "customers",
    baseQuotes: "base-quotes",
    revenueLinesFlat: "revenue-lines-flat",
    engineeringQuotingGrid: "engineering-quoting-grid",
    dashboardForecasting: "dashboard-forecasting",
    dashboardActiveProjects: "dashboard-active-projects",
    dashboardOpportunities: "dashboard-opportunities",
    dashboardCustomers: "dashboard-customers",
    baseSingleCatalog: "base-single-catalog",
    engineeringSingleCatalog: "engineering-single-catalog",
};

const canUseSessionStorage = () => typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";

const buildStorageKey = (gridKey, userId) => `${STORAGE_PREFIX}:${userId || "anonymous"}:${gridKey}`;

export const readGridSessionState = (gridKey, userId) => {
    if (!gridKey || !canUseSessionStorage()) return null;

    try {
        const rawState = window.sessionStorage.getItem(buildStorageKey(gridKey, userId));
        return rawState ? JSON.parse(rawState) : null;
    } catch (error) {
        return null;
    }
};

export const writeGridSessionState = (gridKey, userId, state) => {
    if (!gridKey || !canUseSessionStorage()) return;

    try {
        window.sessionStorage.setItem(buildStorageKey(gridKey, userId), JSON.stringify(state));
    } catch (error) {
        // Ignore storage failures so the grid keeps working even if sessionStorage is unavailable.
    }
};

export const clearGridSessionState = (gridKey, userId) => {
    if (!gridKey || !canUseSessionStorage()) return;

    try {
        window.sessionStorage.removeItem(buildStorageKey(gridKey, userId));
    } catch (error) {
        // Ignore storage failures so the grid keeps working even if sessionStorage is unavailable.
    }
};

export const getPersistableGridState = (state = {}) => {
    const filterModel = state?.filter?.filterModel;
    const sortModel = state?.sorting?.sortModel;
    const columnVisibilityModel = state?.columns?.columnVisibilityModel;
    const paginationModel = state?.pagination?.paginationModel;
    const density = state?.density?.value;

    return {
        ...(filterModel ? { filterModel } : {}),
        ...(sortModel ? { sortModel } : {}),
        ...(columnVisibilityModel ? { columnVisibilityModel } : {}),
        ...(paginationModel ? { paginationModel } : {}),
        ...(density ? { density } : {}),
    };
};

export const buildGridInitialState = (baseState = {}, sessionState = null) => {
    if (!sessionState) return baseState;

    return {
        ...baseState,
        ...(sessionState.filterModel ? {
            filter: {
                ...(baseState.filter || {}),
                filterModel: sessionState.filterModel,
            },
        } : {}),
        ...(sessionState.sortModel ? {
            sorting: {
                ...(baseState.sorting || {}),
                sortModel: sessionState.sortModel,
            },
        } : {}),
        ...(sessionState.columnVisibilityModel ? {
            columns: {
                ...(baseState.columns || {}),
                columnVisibilityModel: sessionState.columnVisibilityModel,
            },
        } : {}),
        ...(sessionState.paginationModel ? {
            pagination: {
                ...(baseState.pagination || {}),
                paginationModel: sessionState.paginationModel,
            },
        } : {}),
        ...(sessionState.density ? { density: sessionState.density } : {}),
    };
};

export const hasActiveFilters = (filterModel = {}) => {
    const activeItems = Array.isArray(filterModel?.items)
        ? filterModel.items.filter((item) => item?.field && item?.operator && item?.value !== undefined && item?.value !== null && item?.value !== "")
        : [];
    const quickFilters = Array.isArray(filterModel?.quickFilterValues)
        ? filterModel.quickFilterValues.filter(Boolean)
        : [];

    return activeItems.length > 0 || quickFilters.length > 0;
};
